/**
 * Builds the account sidebar sections client-side, mirroring the Ruby AccountSidebar concern.
 * Used by the burger menu to show account navigation for logged-in users.
 */

export interface AccountSidebarItem {
  href: string
  label: string
  active: boolean
  count?: number
}

export interface AccountSidebarSection {
  label: string
  items: AccountSidebarItem[]
}

interface BuildAccountSectionsParams {
  t: (key: string) => string
  isSeller: boolean
  hasStripeUid: boolean
  shippingCountry?: string
  hasConsignment?: boolean
}

const isActive = (href: string): boolean => {
  if (typeof window === 'undefined') return false

  const currentPath = window.location.pathname
  const currentSearch = window.location.search

  const url = new URL(href, window.location.origin)
  const pathMatch = currentPath === url.pathname

  if (url.search && pathMatch) {
    const hrefParams = new URLSearchParams(url.search)
    const currentParams = new URLSearchParams(currentSearch)
    let allMatch = true

    hrefParams.forEach((value, key) => {
      // /account/selling without status param defaults to "pending"
      const actual = currentParams.get(key) ||
        (key === 'status' && currentPath.includes('/selling') ? 'pending' : null)
      if (actual !== value) allMatch = false
    })
    return allMatch
  }

  return pathMatch
}

export const buildAccountSections = ({
  t,
  isSeller,
  hasStripeUid,
  shippingCountry,
  hasConsignment = false
}: BuildAccountSectionsParams): AccountSidebarSection[] => {
  const sections: AccountSidebarSection[] = []

  // Listings section — only for sellers
  if (isSeller || hasStripeUid) {
    const activeLabel =
      shippingCountry && shippingCountry !== 'GB'
        ? t('shared.unsold')
        : t('shared.active')

    const listingsItems: AccountSidebarItem[] = [
      {
        href: '/account/selling?status=pending',
        label: t('shared.pending'),
        active: isActive('/account/selling?status=pending')
      },
      {
        href: '/account/selling?status=active',
        label: activeLabel,
        active: isActive('/account/selling?status=active')
      },
      {
        href: '/account/selling?status=sold',
        label: t('shared.completed'),
        active: isActive('/account/selling?status=sold')
      },
      ...(hasConsignment
        ? [
            {
              href: '/account/selling?status=consignment',
              label: t('shared.consignment'),
              active: isActive('/account/selling?status=consignment')
            }
          ]
        : []),
      {
        href: '/account/selling?status=cancelled',
        label: t('shared.cancelled'),
        active: isActive('/account/selling?status=cancelled')
      }
    ]

    sections.push({
      label: t('views.account.sidebar.listings'),
      items: listingsItems
    })
  }

  // Financial section
  sections.push({
    label: t('views.account.sidebar.financial'),
    items: [
      {
        href: '/account',
        label: t('views.account.sidebar.balance'),
        active: isActive('/account')
      },
      {
        href: '/account/transactions',
        label: t('shared.transactions'),
        active: isActive('/account/transactions')
      },
      {
        href: '/account/withdrawals',
        label: t('views.account.sidebar.withdrawals'),
        active: isActive('/account/withdrawals')
      }
    ]
  })

  // Account section
  sections.push({
    label: t('views.account.sidebar.account'),
    items: [
      {
        href: '/account/orders',
        label: t('shared.orders'),
        active: isActive('/account/orders')
      },
      {
        href: '/account/addresses',
        label: t('views.account.show.addresses'),
        active: isActive('/account/addresses')
      },
      {
        href: '/users/edit',
        label: t('shared.account_details'),
        active: isActive('/users/edit')
      },
      {
        href: '/account/stock-notifications',
        label: t('views.account.show.stock'),
        active: isActive('/account/stock-notifications')
      },
      ...(hasConsignment
        ? [
            {
              href: '/account/consignment-shipments',
              label: t('views.account.show.consignment_shipments'),
              active: isActive('/account/consignment-shipments')
            }
          ]
        : [])
    ]
  })

  return sections
}
